import { useState } from "react";
import {
  Ban,
  Plus,
  Trash2,
  Thermometer,
  Save,
  Send,
  FilePlus2,
  ClipboardList,
  Layers,
} from "lucide-react";
import {
  Badge,
  Button,
  Modal,
  FormSection,
  Input,
  SearchAppendRepeater,
  type ModalSize,
  type ModalIconTone,
  type ModalIconVariant,
  type ModalHeaderTone,
  type SearchAppendOption,
  type SearchAppendLineItem,
} from "@/shared/ui";
import { FormField } from "@/shared/ui/FormField";
import { SearchableSelect } from "@/shared/ui/core/SearchableSelect";
import { DatePicker } from "@/shared/ui/core/DatePicker";
import { cn } from "@/shared/lib/cn";

export interface TestUiCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (code: string) => void;
  initialSize?: ModalSize;
}

export function TestUiCreateModal({
  isOpen,
  onClose,
  onSuccess,
  initialSize = "large",
}: TestUiCreateModalProps) {
  const [modalSize, setModalSize] = useState<ModalSize>(initialSize);
  const [customWidth, setCustomWidth] = useState("780px");
  const [modalIconVariant, setModalIconVariant] = useState<ModalIconVariant>("badge");
  const [modalIconTone, setModalIconTone] = useState<ModalIconTone>("primary");
  const [modalHeaderTone, setModalHeaderTone] = useState<ModalHeaderTone>("create");
  const [plant, setPlant] = useState("STVQ");
  const [priority, setPriority] = useState("Ưu tiên cao");
  const [dueDate, setDueDate] = useState("2026-09-30T16:00");
  const [tempCondition, setTempCondition] = useState("-18");
  const [haccpStd, setHaccpStd] = useState("HACCP / ISO 22000");

  const productOptions: SearchAppendOption[] = [
    {
      value: "SP-CT-170220",
      label: "Cá tra fillet đông rời IQF size 170-220g",
      subLabel: "Thùng 10kg · Mạ băng 10%",
      uom: "kg",
      defaultQty: 5000,
      spec: "Mạ băng 10%, bao bì PE hàn kín",
    },
    {
      value: "SP-CT-220UP",
      label: "Cá tra fillet đông rời IQF size >220g",
      subLabel: "Thùng 10kg · Xuất khẩu EU",
      uom: "kg",
      defaultQty: 2500,
      spec: "Mạ băng 10%, đóng thùng 10kg",
    },
    {
      value: "SP-TS-HOSO",
      label: "Tôm sú nguyên con đông lạnh semi-IQF",
      subLabel: "Size 15 con/kg · Cấp đông tiếp xúc",
      uom: "kg",
      defaultQty: 1000,
      spec: "Cấp đông tiếp xúc, bao bì 2kg",
    },
    {
      value: "SP-MC-TUBE",
      label: "Mực nang ống làm sạch đóng khay 500g",
      subLabel: "Khay 500g hút chân không",
      uom: "hộp",
      defaultQty: 800,
      spec: "Đóng khay 500g, -18°C",
    },
    {
      value: "SP-TL-500G",
      label: "Chả cá thát lát rút xương tẩm gia vị",
      subLabel: "Khay 500g · Hàng nội địa",
      uom: "hộp",
      defaultQty: 1200,
      spec: "Tẩm gia vị tiêu ớt, đóng khay 500g",
    },
    {
      value: "SP-BT-BABY",
      label: "Bạch tuộc hai da baby làm sạch",
      subLabel: "IQF size 20-40 con/kg",
      uom: "kg",
      defaultQty: 1500,
      spec: "Rửa khử trùng Ozone, IQF",
    },
  ];

  const [lines, setLines] = useState<SearchAppendLineItem[]>([
    {
      id: "1",
      itemCode: "SP-CT-170220",
      itemName: "Cá tra fillet đông rời IQF size 170-220g",
      qty: 5000,
      uom: "kg",
      spec: "Mạ băng 10%, bao bì PE hàn kín",
    },
    {
      id: "2",
      itemCode: "SP-CT-220UP",
      itemName: "Cá tra fillet đông rời IQF size >220g",
      qty: 2500,
      uom: "kg",
      spec: "Mạ băng 10%, đóng thùng 10kg",
    },
  ]);

  const plantOptions = [
    { value: "STVQ", label: "NM Thủy sản Sóc Trăng (STVQ)" },
    { value: "CT01", label: "Kho Lạnh Tổng Cần Thơ (CT-01)" },
    { value: "VL02", label: "Xưởng Chế biến Vĩnh Long (VL-02)" },
    { value: "HG03", label: "Kho Lạnh Cảng Hậu Giang (HG-03)" },
    { value: "BL01", label: "Trại nuôi Tôm công nghệ cao Bạc Liêu (BL-01)" },
  ];

  const priorityOptions = [
    { value: "Bình thường", label: "Bình thường (Tiêu chuẩn)" },
    { value: "Ưu tiên cao", label: "Ưu tiên cao (Xử lý trong 48h)" },
    { value: "Khẩn cấp", label: "Khẩn cấp (SLA trong 24h)" },
  ];

  const haccpOptions = [
    { value: "HACCP / ISO 22000", label: "HACCP / ISO 22000 (Tiêu chuẩn Quốc gia)" },
    { value: "BRC Global Food Standard", label: "BRC Global Food Standard v9 (Xuất khẩu EU)" },
    { value: "ASC / BAP Farm", label: "ASC / BAP Farm Certified (Thủy sản bền vững)" },
    { value: "VietGAP", label: "VietGAP Nông Thủy sản an toàn" },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSuccess("MO-2026-0320");
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      headerTone={modalHeaderTone}
      title="Lập Lệnh Sản Xuất / Phiếu Mới"
      icon={<FilePlus2 className="size-4" />}
      iconVariant={modalIconVariant}
      iconTone={modalIconTone}
      description="Nhập thông tin phân xưởng, điều kiện HACCP và định mức dòng hàng sản xuất."
      size={modalSize}
      customWidth={modalSize === "custom" ? customWidth : undefined}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Ban className="size-3.5" />}
            onClick={onClose}
          >
            Hủy
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onSuccess("DRAFT-SAVED")}
            leftIcon={<Save className="size-3.5" />}
          >
            Lưu nháp
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleSubmit}
            leftIcon={<Send className="size-3.5" />}
          >
            Tạo & Chuyển duyệt
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4 py-1">
        {/* Controls thanh công cụ: Kích thước, Kiểu icon, Tone màu */}
        <div className="rounded-control border border-border bg-bg/60 p-2.5 space-y-2 text-[11px]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-text-muted">
              <span>Kích thước modal:</span>
              <span className="font-semibold text-text uppercase">{modalSize}</span>
              <span className="text-text-muted font-normal">
                {modalSize === "small" && "(512px)"}
                {modalSize === "medium" && "(672px)"}
                {modalSize === "large" && "(896px)"}
                {modalSize === "custom" && `(${customWidth})`}
              </span>
            </div>
            <div className="inline-flex items-center rounded-control bg-surface p-0.5 border border-border/70">
              {(
                [
                  { id: "small", label: "Small (512px)" },
                  { id: "medium", label: "Medium (672px)" },
                  { id: "large", label: "Large (896px)" },
                  { id: "custom", label: "Custom" },
                ] as const
              ).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setModalSize(item.id)}
                  className={cn(
                    "rounded px-2 py-0.5 font-medium transition-colors cursor-pointer",
                    modalSize === item.id
                      ? "bg-primary text-on-primary font-bold shadow-xs"
                      : "text-text-muted hover:text-text"
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/50 pt-2">
            <div className="flex items-center gap-2">
              <span className="text-text-muted font-medium">Kiểu Icon:</span>
              <div className="inline-flex items-center rounded-control bg-surface p-0.5 border border-border/70">
                {(["badge", "inline"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setModalIconVariant(v)}
                    className={cn(
                      "rounded px-2 py-0.5 font-medium transition-colors cursor-pointer",
                      modalIconVariant === v
                        ? "bg-primary text-on-primary font-bold shadow-xs"
                        : "text-text-muted hover:text-text"
                    )}
                  >
                    {v === "badge" ? "Badge (Khung bo)" : "Inline (Trần)"}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-text-muted font-medium">Tone:</span>
              <div className="inline-flex items-center rounded-control bg-surface p-0.5 border border-border/70 gap-0.5">
                {(["primary", "info", "success", "warning", "danger", "neutral"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setModalIconTone(t)}
                    className={cn(
                      "rounded px-1.5 py-0.5 font-medium transition-colors cursor-pointer",
                      modalIconTone === t
                        ? "bg-primary text-on-primary font-bold shadow-xs"
                        : "text-text-muted hover:text-text"
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/50 pt-2">
            <div className="flex items-center gap-1.5">
              <span className="text-text-muted font-medium">Header Tone:</span>
              <div className="inline-flex items-center rounded-control bg-surface p-0.5 border border-border/70 gap-0.5">
                {(["create", "edit", "status", "warning", "info", "danger", "neutral", "default"] as const).map((ht) => (
                  <button
                    key={ht}
                    type="button"
                    onClick={() => setModalHeaderTone(ht)}
                    className={cn(
                      "rounded px-1.5 py-0.5 font-medium transition-colors cursor-pointer",
                      modalHeaderTone === ht
                        ? "bg-primary text-on-primary font-bold shadow-xs"
                        : "text-text-muted hover:text-text"
                    )}
                  >
                    {ht}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 pt-1">
          {/* Nhóm 1: Thông tin lệnh sản xuất & Phân xưởng */}
          <FormSection
            stepNumber={1}
            title="Thông tin lệnh sản xuất & Phân xưởng"
            icon={<ClipboardList className="size-4" />}
          >
            <div className="grid grid-cols-12 gap-x-4 gap-y-3.5">
              {/* Mã phiếu */}
              <div className="col-span-12 sm:col-span-4">
                <FormField label="Mã phiếu" required hint="Hệ thống tự cấp">
                  <Input
                    disabled
                    value="MO-2026-AUTOGEN"
                    className="font-mono bg-bg/50"
                    size="sm"
                  />
                </FormField>
              </div>

              {/* Phân xưởng */}
              <div className="col-span-12 sm:col-span-8">
                <FormField label="Phân xưởng sản xuất" required>
                  <SearchableSelect
                    size="sm"
                    options={plantOptions}
                    value={plant}
                    onValueChange={(val) => setPlant(val)}
                    placeholder="Chọn phân xưởng..."
                    searchPlaceholder="Nhập tên phân xưởng..."
                  />
                </FormField>
              </div>

              {/* Mức độ ưu tiên */}
              <div className="col-span-12 sm:col-span-6">
                <FormField label="Mức độ ưu tiên">
                  <SearchableSelect
                    size="sm"
                    options={priorityOptions}
                    value={priority}
                    onValueChange={(val) => setPriority(val)}
                    placeholder="Chọn mức độ..."
                    searchPlaceholder="Lọc mức độ..."
                  />
                </FormField>
              </div>

              {/* Hạn hoàn thành */}
              <div className="col-span-12 sm:col-span-6">
                <FormField label="Thời hạn hoàn thành kế hoạch" required>
                  <DatePicker
                    mode="datetime"
                    value={dueDate}
                    onChange={(val) => setDueDate(val)}
                    placeholder="Chọn hạn hoàn thành..."
                  />
                </FormField>
              </div>
            </div>
          </FormSection>

          {/* Nhóm 2: Điều kiện kỹ thuật & Tiêu chuẩn ATTP */}
          <FormSection
            stepNumber={2}
            title="Điều kiện kỹ thuật & Tiêu chuẩn ATTP"
            icon={<Thermometer className="size-4" />}
          >
            <div className="grid grid-cols-12 gap-x-4 gap-y-3.5">
              {/* Nhiệt độ */}
              <div className="col-span-12 sm:col-span-6">
                <FormField label="Nhiệt độ yêu cầu (HACCP)" hint="Ngưỡng chuẩn ≤ -18°C">
                  <Input
                    size="sm"
                    startAdornment={<Thermometer className="size-3.5 text-info-fg" />}
                    value={`${tempCondition}°C`}
                    onChange={(e) => setTempCondition(e.target.value.replace("°C", ""))}
                    placeholder="-18°C"
                    className="font-mono"
                  />
                </FormField>
              </div>

              {/* Tiêu chuẩn ATTP */}
              <div className="col-span-12 sm:col-span-6">
                <FormField label="Tiêu chuẩn ATTP áp dụng">
                  <SearchableSelect
                    size="sm"
                    options={haccpOptions}
                    value={haccpStd}
                    onValueChange={(val) => setHaccpStd(val)}
                    placeholder="Chọn tiêu chuẩn ATTP..."
                    searchPlaceholder="Tìm tiêu chuẩn..."
                  />
                </FormField>
              </div>
            </div>
          </FormSection>

          {/* Section 3: Dynamic Items Repeater using Search + Append */}
          <FormSection
            stepNumber={3}
            title="Chi tiết dòng hàng / Định mức sản xuất"
            icon={<Layers className="size-4" />}
            badge={lines.length > 0 ? `${lines.length} mặt hàng` : undefined}
            badgeTone="primary"
          >
            <SearchAppendRepeater
              borderless
              title=""
              items={lines}
              onChange={setLines}
              options={productOptions}
              searchPlaceholder="Gõ mã SKU, tên mặt hàng hoặc quét barcode để thêm nhanh... (/)"
              summaryUnitLabel="kg"
            />
          </FormSection>
        </div>
      </form>
    </Modal>
  );
}
